import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';
import { CheckCircle, AlertCircle } from 'lucide-react';
import { API_ENDPOINTS } from '../../../utils/config';
import ClientModal from '../../client-management/components/ClientModal';

const AddPropertyModal = ({ isOpen, onClose, onSave, property, mode = 'add', className = "" }) => {
  const [formData, setFormData] = useState({
    client_id: '',
    customer_name: '',
    contact_person_name: '',
    contact_person_phone_num: '',
    property_address: '',
    latitude: '',
    longitude: ''
  });

  const [banks, setBanks] = useState([]);
  const [isLoadingBanks, setIsLoadingBanks] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isClientModalOpen, setIsClientModalOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [errors, setErrors] = useState({});

  // Populate form data when property or banks change
  useEffect(() => {
    if (isOpen && mode === 'edit' && property && banks.length > 0) {

      // Find the client_id by matching the client name
      let clientId = property.client_id || property.bank_id || '';

      // If no client_id, try to find it by matching the client name
      if (!clientId && property.client) {
        const matchingBank = banks.find(bank =>
          bank.label.toLowerCase().includes(property.client.toLowerCase())
        );
        if (matchingBank) {
          clientId = matchingBank.value;
        }
      }

      const formValues = {
        client_id: clientId,
        customer_name: property.customer_name || '',
        contact_person_name: property.contact_person_name || '',
        contact_person_phone_num: Array.isArray(property.contact_person_phone_num)
          ? property.contact_person_phone_num.join('\n')
          : property.contact_person_phone_num || '',
        property_address: property.property_address || '',
        latitude: property.latitude || '',
        longitude: property.longitude || ''
      };
      setFormData(formValues);
    }
  }, [isOpen, mode, property, banks]);

  // Fetch banks when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchBanks();

      // Reset form for add mode
      if (mode === 'add') {
        setFormData({
          client_id: '',
          customer_name: '',
          contact_person_name: '',
          contact_person_phone_num: '',
          property_address: '',
          latitude: '',
          longitude: ''
        });
      }

      setErrors({});
      setSuccessMessage('');
      setErrorMessage('');
    }
  }, [isOpen, mode]);

  const fetchBanks = async () => {
    setIsLoadingBanks(true);
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Accept': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      };

      const response = await axios.get(API_ENDPOINTS.CLIENTS_LIST, { headers });
      const clientsData = Array.isArray(response.data)
        ? response.data
        : (response.data?.results || response.data?.data || response.data?.clients || []);

      // Convert to select options with proper formatting
      const bankOptions = [
        { value: '', label: 'Select Bank/Client' },
        ...clientsData.map(client => ({
          value: client.id,
          label: client.branch
            ? `${client.name} - ${client.branch}`
            : client.name
        }))
      ];

      setBanks(bankOptions);
    } catch (error) {
      setErrorMessage('Failed to load banks. Please try again.');
    } finally {
      setIsLoadingBanks(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.client_id) {
      newErrors.client_id = 'Bank/Client is required';
    }
    if (!formData?.customer_name?.trim()) {
      newErrors.customer_name = 'Customer name is required';
    }
    if (!formData?.property_address?.trim() && !formData?.latitude?.trim() && !formData?.longitude?.trim()) {
      newErrors.property_address = 'Enter property address or latitude/longitude';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      const token = localStorage.getItem('token');
      const formattedPhones = formData.contact_person_phone_num
        ? formData.contact_person_phone_num
            .split(/[,\n]/)
            .map(num => num.trim())
            .filter(num => num !== '')
        : [];
      const formattedData = {
        ...formData,
        contact_person_phone_num: formattedPhones
      };
      const headers = {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      };

      let response;

      if (mode === 'edit') {
        response = await axios.put(
          API_ENDPOINTS.PROPERTY_UPDATE(property?.id),
          formattedData,
          { headers }
        );
      } else {
        response = await axios.post(
          API_ENDPOINTS.PROPERTY_CREATE,
          formattedData,
          { headers }
        );
      }

      const message = response.data?.message || (mode === 'edit' ? 'Property updated successfully' : 'Property created successfully');
      setSuccessMessage(message);

      // Auto close modal after 2 seconds
      setTimeout(() => {
        setSuccessMessage('');
        onSave(response.data);
        handleClose();
      }, 2000);
    } catch (error) {
      const apiError = error?.response?.data?.message ||
        error?.response?.data?.detail ||
        error?.response?.data?.error ||
        error?.message ||
        (mode === 'edit' ? 'Failed to update property. Please try again.' : 'Failed to create property. Please try again.');
      setErrorMessage(apiError);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      client_id: '',
      customer_name: '',
      contact_person_name: '',
      contact_person_phone_num: '',
      property_address: '',
      latitude: '',
      longitude: ''
    });
    setErrors({});
    setSuccessMessage('');
    setErrorMessage('');
    onClose();
  };

  const handleClientSaved = async (newClient) => {
    await fetchBanks();
    if (newClient?.id) {
      handleInputChange('client_id', newClient.id);
    }
    setIsClientModalOpen(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className={`bg-card rounded-lg border border-border shadow-elevation-3 w-full max-w-2xl max-h-[90vh] flex flex-col ${className}`}>
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-border flex-shrink-0">
          <div className="flex items-center space-x-2">
            <Icon name={mode === 'edit' ? 'Edit' : 'Plus'} size={20} color="var(--color-primary)" />
            <h2 className="text-xl font-semibold text-foreground">
              {mode === 'edit' ? 'Edit Property' : 'Add New Property'}
            </h2>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleClose}
          >
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Modal Content */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="overflow-y-auto flex-1 p-6 space-y-6">
            {/* Success/Error Messages */}
            {successMessage && (
              <div className="p-3 rounded-md border border-green-300 bg-green-50 text-green-700">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5" />
                  <p className="text-sm font-medium">{successMessage}</p>
                </div>
                <p className="text-xs text-green-600 mt-1">This modal will close automatically in 2 seconds...</p>
              </div>
            )}
            {errorMessage && (
              <div className="p-3 rounded-md border border-red-300 bg-red-50 text-red-700">
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-5 h-5" />
                  <p className="text-sm">{errorMessage}</p>
                </div>
              </div>
            )}

            {/* Form Fields */}
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-sm font-medium text-foreground">
                    Bank / Client <span className="text-red-500">*</span>
                  </label>
                  <button
                    type="button"
                    onClick={() => setIsClientModalOpen(true)}
                    className="flex items-center gap-1.5 text-xs font-semibold text-white bg-primary hover:bg-primary/90 px-2.5 py-1 rounded-md shadow-sm transition-all hover:shadow-md active:scale-95"
                  >
                    <Icon name="Plus" size={12} />
                    Add Client
                  </button>
                </div>
                <Select
                  options={banks}
                  value={formData?.client_id}
                  onChange={(value) => handleInputChange('client_id', value)}
                  placeholder="Select bank/client"
                  loading={isLoadingBanks}
                  searchable
                  required
                  error={errors?.client_id}
                />
              </div>

              <Input
                label="Customer Name"
                type="text"
                placeholder="Enter customer name (e.g., Krunal)"
                value={formData?.customer_name}
                onChange={(e) => handleInputChange('customer_name', e?.target?.value)}
                error={errors?.customer_name}
                required
              />

              <Input
                label="Contact Person Name"
                type="text"
                placeholder="Enter contact person name (e.g., Ramu)"
                value={formData?.contact_person_name}
                onChange={(e) => handleInputChange('contact_person_name', e?.target?.value)}
                error={errors?.contact_person_name}
              />

              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">Contact Person Phone Number</label>
                <textarea
                  placeholder="Enter phone numbers(e.g., 98450xxxx, 97421xxxx)"
                  value={formData?.contact_person_phone_num}
                  onChange={(e) => handleInputChange('contact_person_phone_num', e?.target?.value)}
                  rows={3}
                  className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none"
                />
                {errors?.contact_person_phone_num && (
                  <p className="text-sm text-destructive">{errors.contact_person_phone_num}</p>
                )}
              </div>

              <Input
                label="Property Location (Address)"
                type="text"
                placeholder="Enter full property address (e.g., #20,RV road,Mysore-560040)"
                value={formData?.property_address}
                onChange={(e) => handleInputChange('property_address', e?.target?.value)}
                error={errors?.property_address}
              />

              <div className="text-xs text-muted-foreground -mt-2 mb-2">
                If address is not available, enter latitude and longitude below
              </div>

              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Latitude"
                  type="text"
                  placeholder="e.g., 12.969114"
                  value={formData?.latitude}
                  onChange={(e) => handleInputChange('latitude', e?.target?.value)}
                  error={errors?.latitude}
                />
                <Input
                  label="Longitude"
                  type="text"
                  placeholder="e.g., 77.537383"
                  value={formData?.longitude}
                  onChange={(e) => handleInputChange('longitude', e?.target?.value)}
                  error={errors?.longitude}
                />
              </div>
            </div>
          </div>

          {/* Modal Footer */}
          <div className="flex items-center justify-end space-x-3 p-6 border-t border-border bg-muted/30 flex-shrink-0">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="default"
              loading={isSubmitting}
              disabled={!!successMessage}
              iconName={successMessage ? "CheckCircle" : "Save"}
              iconPosition="left"
            >
              {successMessage
                ? 'Success!'
                : (mode === 'edit' ? 'Update Property' : 'Add Property')}
            </Button>
          </div>
        </form>
      </div>
      <ClientModal
        isOpen={isClientModalOpen}
        onClose={() => setIsClientModalOpen(false)}
        onSave={handleClientSaved}
        mode="add"
      />
    </div>
  );
};

export default AddPropertyModal;